import styled from '@emotion/native';
import { getReferenceType, Period, PeriodComplete } from '@quromedical/fhir-common';
import { TaskStatus, PartialTaskWithId, TaskUpdate } from '@quromedical/models';
import { compact, first, orderby } from '@quromedical/utils';
import { Col, Row, withVisibility } from 'components/base';
import { CrudForm, SubmissionHandler } from 'components/form';
import { Field } from 'components/types';
import { mapDisplaysToOptions } from 'core/forms';
import { Button, Card, Spacer } from 'design-system/base';
import { FormCard, RowConfig, Tabs } from 'design-system/components';
import { MARGIN } from 'design-system/theme';
import { dateTimeWithoutYearWithSecondsFormatter, shortMonthDateFormatter } from 'helpers';
import React, { useCallback } from 'react';
import { ScrollView, useWindowDimensions } from 'react-native';
import { strings } from 'strings';
import { z } from 'zod';

import {
  statusFilterDisplays,
  statusUpdateDisplays,
  SupportedUpdateStatus,
  taskDisplay,
} from './internal';

export interface ItemNote {
  author?: string;
  date?: string;
  text?: string;
}

export interface ContentProps {
  onClose?: () => void;
  task: PartialTaskWithId;
  onSelect?: () => void;
  onNoteAdd?: (input: TaskUpdate) => void;
  isDialog?: Boolean;
}

interface ActionProps {
  primaryText: string;
  onPrimaryPress?: () => void;

  secondaryText: string;
  onSecondaryPress?: () => void;
}

const BaseAction: React.FC<ActionProps> = ({
  onPrimaryPress,
  onSecondaryPress,
  primaryText,
  secondaryText,
}) => (
  <Row alignItems="center" justifyContent="flex-end">
    {onSecondaryPress ? (
      <Button variant="flat" onPress={onSecondaryPress} text={secondaryText} />
    ) : null}
    <Spacer width={MARGIN.m} />
    {onPrimaryPress ? <Button onPress={onPrimaryPress} text={primaryText} /> : null}
  </Row>
);

const Action = withVisibility<ActionProps>(BaseAction);

const NotesCard = styled(Card)(() => {
  const { height } = useWindowDimensions();

  return {
    maxHeight: height / 2,
  };
});

const getNotes = (task: PartialTaskWithId): ItemNote[] => {
  const noteData = task.notes.map(note => ({
    date: note.time,
    text: note.text,
    author: note.author.display,
  }));

  const sortedNotes = orderby(noteData, note => new Date(note.date).getTime(), 'desc');

  return sortedNotes;
};

const isPeriodComplete = (period: Period): period is PeriodComplete => {
  return (period as PeriodComplete).end !== undefined;
};

const createOverviewRows = (task: PartialTaskWithId): RowConfig[] => {
  const description = task.description;
  const date = task.period?.start;
  const status = task.status;
  const requestedBy = task.requester?.display;
  const lastUpdated = first(getNotes(task));
  const assignedTo = compact(task.performers.map(performer => performer.display));
  const owner = task.owner?.display;
  const updatedBy = lastUpdated?.author;
  const dateUpdated = lastUpdated?.date;
  const requestedPeriodEnd =
    task.requestedPeriod && isPeriodComplete(task.requestedPeriod)
      ? task.requestedPeriod.end
      : undefined;
  const dueDate = requestedPeriodEnd
    ? requestedPeriodEnd && shortMonthDateFormatter(new Date(requestedPeriodEnd))
    : strings.NotSpecified;

  return [
    {
      fields: [
        {
          type: 'text',
          label: strings.TaskLabelDateCreated,
          display: date && shortMonthDateFormatter(new Date(date)),
        },
        {
          type: 'text',
          label: strings.TaskLabelStatus,
          display: status && statusDisplays[status],
          fallback: 'Unknown',
        },
        {
          type: 'text',
          label: strings.TaskLabelOwner,
          display: owner,
        },
      ],
    },
    {
      fields: [
        {
          type: 'text',
          label: strings.TaskLabelDescription,
          display: description,
        },
        {
          type: 'text',
          label: strings.TaskLabelRequestedBy,
          display: requestedBy,
        },
        {
          type: 'list',
          label: strings.TaskLabelAssignedTo,
          display: assignedTo,
        },
      ],
    },
    {
      fields: [
        {
          type: 'text',
          label: strings.TaskLabelDueDate,
          display: dueDate,
        },
        {
          type: 'text',
          label: strings.TaskLabelDateUpdated,
          display: dateUpdated && shortMonthDateFormatter(new Date(dateUpdated)),
        },
        {
          type: 'text',
          label: strings.TaskLabelUpdatedBy,
          display: updatedBy,
        },
      ],
    },
  ];
};

const createNoteRows = (note: ItemNote): RowConfig[] => [
  {
    fields: [
      {
        type: 'text',
        label: '',
        display: note.date ? dateTimeWithoutYearWithSecondsFormatter(new Date(note.date)) : '',
      },
      {
        type: 'text',
        label: '',
        display: note.author,
      },
    ],
  },
  {
    fields: [
      {
        type: 'text',
        label: '',
        display: note.text,
      },
    ],
  },
];

interface FormData {
  status: TaskStatus;
  dueDate: Date;
  text: string;
}
const statusOptions = mapDisplaysToOptions<SupportedUpdateStatus>(statusUpdateDisplays);

const formRefinement = (
  obj: {
    status?: string;
    dueDate?: string;
    text: string;
  },
  ctx: z.RefinementCtx
) => {
  const { status, dueDate } = obj;

  if (!status && !dueDate) {
    ctx.addIssue({
      code: 'custom',
      message: 'Either Status or a DueDate is required',
      path: ['status'],
    });
    return;
  }
};

const formSchema = z
  .object({
    status: z.string().optional(),
    dueDate: z.string().optional(),
    text: z.string(),
  })
  .superRefine(formRefinement);

const statusDisplays: Partial<Record<TaskStatus, string>> = statusFilterDisplays;

const formFields: Field<FormData>[] = [
  {
    subfields: [
      {
        key: 'status',
        type: 'combobox-single',
        fetcher: statusOptions,
        label: strings.TaskLabelStatus,
      },
    ],
  },
  {
    subfields: [
      {
        key: 'dueDate',
        type: 'date-component',
        maskType: 'date',
        label: strings.TaskLabelDueDate,
        validRange: { startDate: new Date() },
      },
    ],
  },
  {
    subfields: [
      {
        key: 'text',
        type: 'text-box',
        label: strings.TaskLabelNotes,
      },
    ],
  },
];

export const Content: React.FC<ContentProps> = ({
  task,
  onSelect,
  onNoteAdd,
  onClose,
  isDialog,
}) => {
  const notes = getNotes(task);
  const target = task.focus || task.for;
  const targetType = getReferenceType(target);

  const targetDisplay = targetType && taskDisplay[targetType];
  const hasTargetDisplay = Boolean(targetDisplay);

  const ActionButtonText = targetDisplay || strings.TaskButtonViewItem;

  const showActionButtons = (!hasTargetDisplay && isDialog) || hasTargetDisplay;

  const overviewRows = createOverviewRows(task);

  const handleSubmit = useCallback<SubmissionHandler<FormData>>(
    async data => {
      const { text, status, dueDate } = data;
      try {
        await onNoteAdd?.({ id: task.id, note: text, status, dueDate });
        onClose?.();
        return {};
      } catch (error) {
        return {
          error,
        };
      }
    },
    [onNoteAdd, task.id, onClose]
  );

  return (
    <Tabs.Group>
      <Tabs.Screen title="Overview">
        <Col>
          <FormCard paddingHorizontal={0} cardColor="transparent" rows={overviewRows} />
          <Action
            onPrimaryPress={onSelect}
            primaryText={ActionButtonText}
            onSecondaryPress={onClose}
            secondaryText={strings.ButtonTextClose}
            isVisible={showActionButtons}
          />
        </Col>
      </Tabs.Screen>
      <Tabs.Screen title={strings.TaskLabelNotes}>
        <NotesCard color="transparent" paddingHorizontal={0}>
          <ScrollView>
            {notes.map((note, index) => (
              <FormCard
                key={index}
                paddingHorizontal={0}
                cardColor="transparent"
                borderColor={index ? 'divider' : undefined}
                rows={createNoteRows(note)}
              />
            ))}
          </ScrollView>
        </NotesCard>
        <Action
          onPrimaryPress={onSelect}
          primaryText={targetDisplay || strings.TaskButtonViewItem}
          onSecondaryPress={onClose}
          secondaryText={strings.ButtonTextClose}
        />
      </Tabs.Screen>
      <Tabs.Screen title={strings.TaskButtonAddNote}>
        <CrudForm<FormData>
          initialValues={{}}
          onSubmit={handleSubmit}
          showSecondarySubmitButton
          buttonText={strings.TaskButtonAddNote}
          secondaryButtonText={strings.ButtonTextClose}
          onSecondarySubmit={onClose}
          cardProps={{
            paddingHorizontal: 0,
            paddingVertical: 0,
            color: 'transparent',
            unsetZIndex: true,
          }}
          validationSchema={formSchema}
          fields={formFields}
        />
      </Tabs.Screen>
    </Tabs.Group>
  );
};
